
import _memoize from "lodash/memoize"

const test_val = "__storage_test__"

/* eslint-disable no-unused-vars */
export enum GetDataOpResultTypes {
  DATA_RETRIEVED = "DATA_RETRIEVED",

  DATA_NOT_FOUND    = "DATA_NOT_FOUND",
  FEATURE_DISABLED  = "FEATURE_DISABLED",
}
/* eslint-enable no-unused-vars */
interface GetDataResult {
  data: string | null,
  result_type: GetDataOpResultTypes,
}

/* eslint-disable no-unused-vars */
export enum SetDataOpResultTypes {
  DATA_IS_SET = "DATA_IS_SET",

  FEATURE_DISABLED  = "FEATURE_DISABLED",
  STORAGE_FULL      = "STORAGE_FULL",
  UNKNOWN_ERROR     = "UNKNOWN_ERROR",
}
/* eslint-enable no-unused-vars */
export interface SetDataResult {
  result_type: SetDataOpResultTypes,
  exception?: Error,
}

/* eslint-disable no-unused-vars */
export enum RemoveDataOpResultTypes {
  DATA_REMOVED = "DATA_REMOVED",

  FEATURE_DISABLED  = "FEATURE_DISABLED",
  UNKNOWN_ERROR     = "UNKNOWN_ERROR",
}
/* eslint-enable no-unused-vars */
interface RemoveDataResult {
  result_type: RemoveDataOpResultTypes,
  exception?: Error,
}


const isStorageFull = (e: Error, storage: Storage): boolean => {
  if (storage == null) { return false }

  return (((e instanceof DOMException) && (
    // everything except Firefox
    e.code === 22 ||
    // Firefox
    e.code === 1014 ||
    // test name field too, because code might not be present
    // everything except Firefox
    e.name === "QuotaExceededError" ||
    // Firefox
    e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
    // acknowledge QuotaExceededError only if there's something already stored
    storage.length !== 0))
}


// Not just supported by browser or not
// But also enabled by user
//
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
const isLocalStorageEnabled = _memoize((): boolean => {
  let storage
  try {
    storage = window.localStorage
  }
  catch (e) {
    // If there is error accessing this object
    // Mostly SecurityError
    // Then it's disabled
    return false
  }

  if (storage == null) { return false }

  try {
    storage.setItem(test_val, test_val)
    storage.removeItem(test_val)
    return true
  }
  catch (e) {
    return (e instanceof DOMException) && (
      // everything except Firefox
      e.code === 22 ||
      // Firefox
      e.code === 1014 ||
      // test name field too, because code might not be present
      // everything except Firefox
      e.name === "QuotaExceededError" ||
      // Firefox
      e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage.length !== 0
  }
})


const getDataFromLocalStorage = (key: string): GetDataResult => {
  if (! isLocalStorageEnabled()) {
    return {
      data:         null,
      result_type:  GetDataOpResultTypes.FEATURE_DISABLED,
    }
  }

  const storage = window.localStorage
  const data = storage.getItem(key)
  if (data === null) {
    return {
      data:         null,
      result_type:  GetDataOpResultTypes.DATA_NOT_FOUND,
    }
  }
  // It's possible for us to set a `null` or `undefined` into the storage
  // So we handle those strange cases here
  if (data === "null" || data === "undefined") {
    return {
      data:         null,
      result_type:  GetDataOpResultTypes.DATA_NOT_FOUND,
    }
  }

  return {
    data:         data,
    result_type:  GetDataOpResultTypes.DATA_RETRIEVED,
  }
}

const setDataToLocalStorage = (key: string, data: string | null): SetDataResult => {
  if (! isLocalStorageEnabled()) {
    return {
      result_type: SetDataOpResultTypes.FEATURE_DISABLED,
    }
  }

  if (typeof data === "undefined") {
    throw new Error(`invalid data "undefined" passed in for key <${key}>`)
  }

  if (data === null) {
    throw new Error(`invalid data "null" passed in for key <${key}>`)
  }

  const storage = window.localStorage
  try {
    storage.setItem(key, data)
    return {
      result_type: SetDataOpResultTypes.DATA_IS_SET,
    }
  }
  catch (e) {
    if (isStorageFull(e, storage)) {
      return {
        result_type:  SetDataOpResultTypes.STORAGE_FULL,
        exception:    e,
      }
    }
    return {
      result_type:  SetDataOpResultTypes.UNKNOWN_ERROR,
      exception:    e,
    }
  }
}

const removeDataFromLocalStorage = (key: string): RemoveDataResult => {
  if (! isLocalStorageEnabled()) {
    return {
      result_type: RemoveDataOpResultTypes.FEATURE_DISABLED,
    }
  }

  const storage = window.localStorage
  try {
    storage.removeItem(key)
    return {
      result_type: RemoveDataOpResultTypes.DATA_REMOVED,
    }
  }
  catch (e) {
    return {
      result_type:  RemoveDataOpResultTypes.UNKNOWN_ERROR,
      exception:    e,
    }
  }
}


export {
  isLocalStorageEnabled,

  getDataFromLocalStorage,

  setDataToLocalStorage,

  removeDataFromLocalStorage,
}
